// Lib
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';

// Components
import TitleContainer from '../TitleContainer';
import useApprovals from '../../../pages/Approvals/hooks/useApprovals';
import OrderInfo from '../OrderQty/OrderInfo';
import ApproveResponse from '../OrderQty/ApproveResponse';
import Button from '../../Button';
import Spinner from '../../Spinner';
import ProductsList from '../ProductsList';
import Select from '../../Field/Select';

// Hooks
import useUI from '../../../hooks/useUI';
import ApprovalInfo from '../ApprovalInfo';
import useMethodApprovalInfo from '../../../pages/Approvals/hooks/useMethodApprovalInfo';
import ErrorMessage from '../../Error/ErrorMessage';

import { useStyles } from './ShippingMethodStyles';

const ShippingMethod = () => {
  const { classes } = useStyles();
  const {
    onChangeMethod,
    approvals,
    fullName,
    onChangeResponse,
    onSubmit,
    isSaving,
    onOpenShippingModal,
    isSubmitDisabled,
    isMethodApproved,
    comment,
    apiError,
  } = useApprovals(true);
  const { isDesktopMode } = useUI();

  const { order, approvals: approvalsData } = approvals;
  const { status, approvalDate, approverEmail, approverFullName } =
    useMethodApprovalInfo(approvalsData?.shipment_method || {});

  const addressesCount = useMemo(
    () => order?.addresses.length,
    [order?.addresses],
  );

  return (
    <>
      <TitleContainer
        isApproved={isMethodApproved}
        fullName={fullName}
        message="Order shipping method is automatically approved after 6 hours if no action is taken."
      />

      <OrderInfo
        isMethodPage
        isApproved={isMethodApproved}
        className={classes.orderInfo}
        onOpenShippingModal={onOpenShippingModal}
        addressesCount={addressesCount}
        order={order}
      />

      {!isDesktopMode && (
        <>
          <Typography mb={0.5} variant="h2" className={classes.titleSelect}>
            Shipping method
          </Typography>
          {!isMethodApproved ? (
            <Select
              onChange={onChangeMethod}
              className={classes.select}
              classic
              initialValue={order?.shipment_method}
              items={approvals.shippingMethods}
            />
          ) : (
            <>
              <div className={classes.method}>
                {approvals?.order?.shipment_method}
              </div>
              <div className={classes.status}>Approved</div>
            </>
          )}
        </>
      )}

      <ApproveResponse
        isApproved={isMethodApproved}
        isRequired
        onChangeResponse={onChangeResponse}
        response={comment || approvals.approveResponse}
      />

      {isMethodApproved && (
        <ApprovalInfo
          approvals={[
            {
              approverId: approverEmail,
              approverFullName: approverFullName,
              approverEmail: approverEmail,
              approvalDate: approvalDate,
            },
          ]}
          title={`${status} by`}
        />
      )}

      {apiError && <ErrorMessage message={apiError} />}
      {!isMethodApproved && (
        <Button
          disabled={isSubmitDisabled}
          loading={isSaving}
          loadingIndicator={<Spinner size={20} height={24} />}
          onClick={onSubmit}
          className={classes.approveButton}>
          Approve
        </Button>
      )}

      <Typography
        mt={{ lg: 3.5, xs: 3 }}
        mb={{ lg: 0.5, md: 1.5, xs: 2 }}
        className={classes.h2}
        variant="h2">
        Products
      </Typography>
      <ProductsList
        isMethodPage
        addressesCount={addressesCount}
        approvals={approvals}
      />
    </>
  );
};

export default ShippingMethod;
